import {
  CTCSS_TONES,
  DTCS_CODES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const BLOCK_SIZE = 16
const MEMORY_SIZE = 0x2000
const CHANNEL_COUNT = 199
const NAME_LENGTH = 7
const TONES = [
  ...Array.from({ length: 8 }, (_, index) => 56 + index),
  ...CTCSS_TONES,
].sort((left, right) => left - right)
const DCS_CODES = [17, 50, 645, ...DTCS_CODES].sort((left, right) => left - right)

type LeixenSpec = {
  id: string
  vendor: 'Leixen' | 'Jetstream' | 'LUITON'
  model: string
  summary: string
  channelStart: number
  nameStart: number
  powerLevels: readonly string[]
  widePower: boolean
}

export type LeixenProfile = {
  protocol: 'leixen'
  id: string
  vendor: 'Leixen' | 'Jetstream' | 'LUITON'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 199
  nameLength: 7
  memorySize: number
  channelStart: number
  nameStart: number
  powerLevels: readonly string[]
  widePower: boolean
}

const SPECS: readonly LeixenSpec[] = [
  {
    id: 'leixen-vv-898',
    vendor: 'Leixen',
    model: 'VV-898',
    summary: 'Leixen VV-898 dual-band mobile.',
    channelStart: 0x0d00,
    nameStart: 0x19b0,
    powerLevels: ['Low', 'High'],
    widePower: false,
  },
  {
    id: 'jetstream-jt270m',
    vendor: 'Jetstream',
    model: 'JT270M',
    summary: 'Jetstream JT270M dual-band mobile.',
    channelStart: 0x0d00,
    nameStart: 0x19b0,
    powerLevels: ['Low', 'High'],
    widePower: false,
  },
  {
    id: 'luiton-lt-898uv',
    vendor: 'LUITON',
    model: 'LT-898UV',
    summary: 'LUITON LT-898UV dual-band mobile.',
    channelStart: 0x0d00,
    nameStart: 0x19b0,
    powerLevels: ['Low', 'High'],
    widePower: false,
  },
  {
    id: 'jetstream-jt270mh',
    vendor: 'Jetstream',
    model: 'JT270MH',
    summary: 'Jetstream JT270MH dual-band mobile.',
    channelStart: 0x0c00,
    nameStart: 0x1900,
    powerLevels: ['Low', 'Mid', 'High'],
    widePower: true,
  },
  {
    id: 'leixen-vv-898s',
    vendor: 'Leixen',
    model: 'VV-898S',
    summary: 'Leixen VV-898S dual-band mobile.',
    channelStart: 0x0d00,
    nameStart: 0x19b0,
    powerLevels: ['Low', 'Mid', 'High'],
    widePower: true,
  },
  {
    id: 'leixen-vv-898e',
    vendor: 'Leixen',
    model: 'VV-898E',
    summary: 'Leixen VV-898E dual-band mobile.',
    channelStart: 0x0d00,
    nameStart: 0x19b0,
    powerLevels: ['Low', 'Mid', 'High'],
    widePower: true,
  },
]

export const LEIXEN_PROFILES: readonly LeixenProfile[] = SPECS.map((spec) => ({
  protocol: 'leixen',
  id: spec.id,
  vendor: spec.vendor,
  model: spec.model,
  summary: spec.summary,
  tags: ['Experimental', 'Read + write', '199 channels', '7-character names'],
  baudRate: 9600,
  channelMinimum: 1,
  channelMaximum: CHANNEL_COUNT,
  nameLength: NAME_LENGTH,
  memorySize: MEMORY_SIZE,
  channelStart: spec.channelStart,
  nameStart: spec.nameStart,
  powerLevels: spec.powerLevels,
  widePower: spec.widePower,
}))

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: 'Low',
    skip: false,
  }
}

function decodeBcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  return value
}

function encodeBcd(value: number, size: number): Uint8Array {
  const result = new Uint8Array(size)
  for (let index = size - 1; index >= 0; index -= 1) {
    const part = value % 100
    result[index] = ((Math.floor(part / 10) << 4) | (part % 10)) & 0xff
    value = Math.floor(value / 100)
  }
  return result
}

type DecodedTone = {
  mode: 'none' | 'ctcss' | 'dtcs'
  ctcss: number | null
  dtcs: number | null
  polarity: 'N' | 'R'
}

function decodeTone(mode: number, value: number): DecodedTone {
  if (mode === 1) {
    return { mode: 'ctcss', ctcss: TONES[value - 1] ?? null, dtcs: null, polarity: 'N' }
  }
  if (mode === 2 || mode === 3) {
    return {
      mode: 'dtcs',
      ctcss: null,
      dtcs: DCS_CODES[value - 1] ?? null,
      polarity: mode === 3 ? 'R' : 'N',
    }
  }
  return { mode: 'none', ctcss: null, dtcs: null, polarity: 'N' }
}

function encodeTone(channel: WebChannel): readonly [number, number, number] {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = TONES.indexOf(channel.ctcss as (typeof TONES)[number])
    if (index < 0) throw new Error('That CTCSS tone is not supported by this radio.')
    return [1, index + 1, index + 1]
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = (DCS_CODES as readonly number[]).indexOf(channel.dtcs)
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    const mode = channel.dtcsPolarity === 'R' ? 3 : 2
    return [mode, index + 1, index + 1]
  }
  return [0, 0, 0]
}

function toneMatches(channel: WebChannel, tone: DecodedTone): boolean {
  if (channel.toneMode === 'none') return tone.mode === 'none'
  if (channel.toneMode === 'ctcss') {
    return tone.mode === 'ctcss' && tone.ctcss === channel.ctcss
  }
  return (
    tone.mode === 'dtcs' &&
    tone.dtcs === channel.dtcs &&
    tone.polarity === channel.dtcsPolarity
  )
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff || value < 0x20 ? 0x20 : value)),
  ).trim()
}

export function parseLeixenChannel(
  image: Uint8Array,
  profile: LeixenProfile,
  number: number,
): WebChannel {
  const offset = profile.channelStart + (number - 1) * 16
  const raw = image.slice(offset, offset + 16)
  if (raw.byteLength !== 16 || raw[0] === 0xff) return emptyChannel(number)
  const rxFrequency = decodeBcd(raw.slice(0, 4)) * 10
  if (!rxFrequency) return emptyChannel(number)
  const txFrequency = raw[4] === 0xff ? null : decodeBcd(raw.slice(4, 8)) * 10
  const txTone = decodeTone((raw[11] >> 6) & 0x03, raw[10])
  const rxtone = decodeTone((raw[9] >> 6) & 0x03, raw[8])
  const selected = txTone.mode === 'none' ? rxtone : txTone
  const nameOffset = profile.nameStart + (number - 1) * 8
  return {
    number,
    empty: false,
    name: decodeName(image.slice(nameOffset, nameOffset + 7)),
    rxFrequency,
    txFrequency,
    toneMode: selected.mode,
    ctcss: selected.ctcss,
    dtcs: selected.dtcs,
    dtcsPolarity: selected.polarity,
    mode: profile.widePower ? raw[13] & 0x01 ? 'FM' : 'NFM' : raw[14] & 0x01 ? 'FM' : 'NFM',
    power: profile.widePower
      ? profile.powerLevels[raw[14] & 0x03] ?? 'Low'
      : raw[14] & 0x02
        ? profile.powerLevels[1] ?? 'High'
        : profile.powerLevels[0],
    skip: (raw[13] & 0x80) !== 0,
  }
}

export function parseLeixenChannels(
  image: Uint8Array,
  profile: LeixenProfile,
): WebChannel[] {
  return Array.from(
    { length: CHANNEL_COUNT },
    (_, index) => parseLeixenChannel(image, profile, index + 1),
  )
}

export function applyLeixenChannel(
  image: Uint8Array,
  profile: LeixenProfile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > CHANNEL_COUNT) {
    throw new Error('Channel must be between 1 and 199.')
  }
  const offset = profile.channelStart + (channel.number - 1) * 16
  const raw = image.subarray(offset, offset + 16)
  const nameOffset = profile.nameStart + (channel.number - 1) * 8
  if (channel.empty) {
    raw.fill(0xff)
    image.fill(0xff, nameOffset, nameOffset + 7)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  if (raw[0] === 0xff) raw.fill(0xff, 8)
  raw.set(encodeBcd(Math.round(channel.rxFrequency / 10), 4), 0)
  if (channel.txFrequency) {
    raw.set(encodeBcd(Math.round(channel.txFrequency / 10), 4), 4)
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const existingTx = decodeTone((raw[11] >> 6) & 0x03, raw[10])
  const existingRx = decodeTone((raw[9] >> 6) & 0x03, raw[8])
  if (
    !toneMatches(channel, existingTx) ||
    (!toneMatches(channel, existingRx) && existingRx.mode !== 'none')
  ) {
    const [mode, txValue, rxValue] = encodeTone(channel)
    raw[9] = mode << 6
    raw[10] = txValue
    raw[11] = mode << 6
    raw[8] = rxValue
  }
  if (profile.widePower) {
    raw[13] = (raw[13] & 0xfe) | (channel.mode === 'FM' ? 1 : 0)
    raw[14] =
      (raw[14] & 0xfc) |
      (Math.max(0, profile.powerLevels.indexOf(channel.power)) & 0x03)
  } else {
    raw[14] =
      (raw[14] & 0xfc) |
      (channel.mode === 'FM' ? 1 : 0) |
      (channel.power === profile.powerLevels[1] ? 2 : 0)
  }
  raw[13] = channel.skip ? raw[13] | 0x80 : raw[13] & 0x7f
  const name = channel.name.slice(0, 7).padEnd(7, ' ')
  for (let index = 0; index < 7; index += 1) image[nameOffset + index] = name.charCodeAt(index)
}

function frame(command: number, address: number, data = new Uint8Array()): Uint8Array {
  const payload = new Uint8Array(2 + data.length)
  payload[0] = (address >> 8) & 0xff
  payload[1] = address & 0xff
  payload.set(data, 2)
  const result = new Uint8Array(3 + payload.length)
  result[0] = command
  result[1] = payload.length
  result.set(payload, 2)
  let xor = 0
  for (let index = 0; index < result.length - 1; index += 1) xor ^= result[index]
  result[result.length - 1] = xor
  return result
}

async function handshake(
  profile: LeixenProfile,
  transport: RadioTransport,
): Promise<void> {
  await transport.write(Uint8Array.from([0x02, 0x06, ...new TextEncoder().encode('LEIXEN'), 0x17]))
  const identity = await transport.readExact(9, 3000)
  if (new TextDecoder().decode(identity).toLowerCase() !== '\u0006\u0006leixen\u0013') {
    throw new Error(`The radio did not identify as ${profile.model}.`)
  }
  await transport.write(Uint8Array.from([0x06, 0x00, 0x06]))
  const ack = await transport.readExact(3, 3000)
  if (new TextDecoder().decode(ack) !== '\u0006\u0000\u0006') {
    throw new Error('The radio rejected Leixen clone mode.')
  }
}

async function finish(transport: RadioTransport): Promise<void> {
  await transport.write(Uint8Array.from([0x64, 0x01, 0x6f, 0x0a]))
  await transport.readExact(8, 1000)
}

export async function readLeixenRadioWithTransport(
  profile: LeixenProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await handshake(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      await transport.write(frame(0x52, address, Uint8Array.of(0x10)))
      const header = await transport.readExact(4, 3000)
      const length = header[1] - 2
      const data = await transport.readExact(length + 1, 3000)
      image.set(data.slice(0, length), address)
      onProgress?.({ current: index + 1, total, label: 'Reading Leixen memory' })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await finish(transport)
  }
}

export async function writeLeixenRadioWithTransport(
  profile: LeixenProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) throw new Error('The selected image is smaller than Leixen memory.')
  const ranges = [
    [0x0000, 0x013f], [0x0148, 0x0167], [0x0184, 0x018f],
    [0x0190, 0x01cf], [0x0900, 0x090f], [0x0920, 0x0927],
    [profile.channelStart, 0x2000],
  ] as const
  const addresses = ranges.flatMap(([start, end]) =>
    Array.from({ length: Math.ceil((end - start) / BLOCK_SIZE) }, (_, index) => start + index * BLOCK_SIZE),
  )
  await handshake(profile, transport)
  try {
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await transport.write(frame(0x57, address, image.slice(address, address + 16)))
      await transport.write(Uint8Array.from([0x06, 0x00, 0x06]))
      const ack = await transport.readExact(3, 3000)
      if (new TextDecoder().decode(ack) !== '\u0006\u0000\u0006') {
        throw new Error(`The radio rejected Leixen block 0x${address.toString(16)}.`)
      }
      onProgress?.({ current: index + 1, total: addresses.length, label: 'Writing Leixen memory' })
    }
  } finally {
    await finish(transport)
  }
}
